import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { Helmet } from 'react-helmet';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import Accordion from './Accordion';
import CompetitorComparison from './CompetitorComparison';
import Content from './Content';
import CustomerReviews from './CustomerReviews';
import FeaturedCommunityStories from './FeaturedCommunityStories';
import FeaturedLogos from './FeaturedLogos';
import FeaturedPlans from './FeaturedPlans';
import InstagramFeed from './InstagramFeed';
import LocationMap from './LocationMap';
import VideoContentBanner from './VideoContentBanner';
import ImageContentBannerSlider from './ImageContentBannerSlider';

const ModularBlocks = ({ items }) => {
  const {
    contentfulSitewide: { trustpilotScore },
  } = useStaticQuery(graphql`
    query ModularBlocksQuery {
      contentfulSitewide {
        trustpilotScore
      }
    }
  `);

  const hasItems = items && items.length > 0;
  const accordionFaqs = [];

  if (hasItems) {
    items
      .filter(
        item =>
          item.internal.type === 'ContentfulAccordionModularBlock' &&
          item.isFaqs === true,
      )
      .forEach(({ accordionItems }) => accordionFaqs.push(...accordionItems));
  }

  return (
    <>
      {accordionFaqs.length > 0 && (
        <Helmet
          script={[
            {
              type: 'application/ld+json',
              innerHTML: `{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                  ${accordionFaqs.map(({ heading, content }) =>
                    JSON.stringify({
                      '@type': 'Question',
                      name: heading,
                      acceptedAnswer: {
                        '@type': 'Answer',
                        text: documentToHtmlString(JSON.parse(content.raw)),
                      },
                    }),
                  )}
                ]
              }`,
            },
          ]}
        />
      )}
      {hasItems &&
        items.map(item => (
          <React.Fragment key={item.id}>
            {item.internal.type === 'ContentfulAccordionModularBlock' && (
              <Accordion
                heading={item.heading}
                text={item.text}
                items={item.accordionItems}
                link={item.link}
                isFaqs={item.isFaqs}
              />
            )}
            {item.internal.type ===
              'ContentfulCompetitorComparisonModularBlock' && (
              <CompetitorComparison
                heading={item.heading}
                text={item.text}
                rows={[
                  {
                    name: 'Ethical Consumer Best Buy',
                    items: [
                      item.ecotalkEthicalConsumerBestBuy,
                      item.threeEthicalConsumerBestBuy,
                      item.o2EthicalConsumerBestBuy,
                      item.vodafoneEthicalConsumerBestBuy,
                    ],
                  },
                  {
                    name: 'Price Increase 2024',
                    items: [
                      `${item.ecotalkPriceIncrease2024}%`,
                      `${item.threePriceIncrease2024}%`,
                      `${item.o2PriceIncrease2024}%`,
                      `${item.vodafonePriceIncrease2024}%`,
                    ],
                  },
                  {
                    name: 'UK Average Download Speeds*',
                    items: [
                      `${item.ecotalkUkAverageDownloadSpeeds}mbps`,
                      `${item.threeUkAverageDownloadSpeeds}mbps`,
                      `${item.o2UkAverageDownloadSpeeds}mbps`,
                      `${item.vodafoneUkAverageDownloadSpeeds}mbps`,
                    ],
                  },
                  {
                    name: "UK's Best Network 2024",
                    items: [
                      item.ecotalkUksBestNetwork2024,
                      item.threeUksBestNetwork2024,
                      item.o2UksBestNetwork2024,
                      item.vodafoneUksBestNetwork2024,
                    ],
                  },
                  {
                    name: 'Trustpilot Score**',
                    items: [
                      trustpilotScore,
                      item.threeTrustpilotScore,
                      item.o2TrustpilotScore,
                      item.vodafoneTrustpilotScore,
                    ],
                  },
                ]}
                footnote={item.footnote.text}
              />
            )}
            {item.internal.type === 'ContentfulContentModularBlock' && (
              <Content
                heading={item.heading}
                text={item.text}
                content={item.content}
              />
            )}
            {item.internal.type === 'ContentfulCustomerReviewsModularBlock' && (
              <CustomerReviews
                heading={item.heading}
                items={item.customerReviews}
              />
            )}
            {item.internal.type === 'ContentfulFeaturedLogosModularBlock' && (
              <FeaturedLogos items={item.logos} />
            )}
            {item.internal.type === 'ContentfulFeaturedPlansModularBlock' && (
              <FeaturedPlans
                heading={item.heading}
                text={item.text}
                items={item.plans}
              />
            )}
            {item.internal.type ===
              'ContentfulImageContentBannerSliderModularBlock' && (
              <ImageContentBannerSlider
                heading={item.heading}
                items={item.contentBannerSlides}
                link={item.link}
              />
            )}
            {item.internal.type === 'ContentfulInstagramFeedModularBlock' && (
              <InstagramFeed heading={item.heading} text={item.text} />
            )}
            {item.internal.type ===
              'ContentfulLatestCommunityStoriesModularBlock' && (
              <FeaturedCommunityStories heading={item.heading} />
            )}
            {item.internal.type === 'ContentfulMapLocationsModularBlock' && (
              <LocationMap
                heading={item.heading}
                text={item.text}
                content={item.content}
              />
            )}
            {item.internal.type ===
              'ContentfulVideoContentBannerModularBlock' && (
              <VideoContentBanner
                video={item.video}
                content={item.content}
                link={item.link}
              />
            )}
          </React.Fragment>
        ))}
    </>
  );
};

export default ModularBlocks;

export const ModularBlockFragments = graphql`
  fragment AccordionModularBlockFragment on ContentfulAccordionModularBlock {
    id
    internal {
      type
    }
    heading
    text
    accordionItems {
      heading
      content {
        raw
      }
    }
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
    isFaqs
  }

  fragment CompetitorComparisonModularBlockFragment on ContentfulCompetitorComparisonModularBlock {
    id
    internal {
      type
    }
    heading
    text
    ecotalkEthicalConsumerBestBuy
    threeEthicalConsumerBestBuy
    o2EthicalConsumerBestBuy
    vodafoneEthicalConsumerBestBuy
    ecotalkPriceIncrease2024
    threePriceIncrease2024
    o2PriceIncrease2024
    vodafonePriceIncrease2024
    ecotalkUkAverageDownloadSpeeds
    threeUkAverageDownloadSpeeds
    o2UkAverageDownloadSpeeds
    vodafoneUkAverageDownloadSpeeds
    ecotalkUksBestNetwork2024
    threeUksBestNetwork2024
    o2UksBestNetwork2024
    vodafoneUksBestNetwork2024
    threeTrustpilotScore
    o2TrustpilotScore
    vodafoneTrustpilotScore
    footnote {
      text: footnote
    }
  }

  fragment ContentModularBlockFragment on ContentfulContentModularBlock {
    id
    internal {
      type
    }
    heading
    text
    content {
      raw
      references {
        contentful_id
        images {
          ...ContentImageSliderImageFragment
        }
        flip
      }
    }
  }

  fragment CustomerReviewsModularBlockFragment on ContentfulCustomerReviewsModularBlock {
    id
    internal {
      type
    }
    heading
    customerReviews {
      name
      heading
      quote {
        quote
      }
      dateOfExperience(formatString: "D MMMM YYYY")
    }
  }

  fragment FeaturedLogosModularBlockFragment on ContentfulFeaturedLogosModularBlock {
    id
    internal {
      type
    }
    logos {
      mimeType
      url
      title
    }
  }

  fragment FeaturedPlansModularBlockFragment on ContentfulFeaturedPlansModularBlock {
    id
    internal {
      type
    }
    heading
    text
    plans {
      ...PlanCardFragment
    }
  }

  fragment ImageContentBannerSliderModularBlockFragment on ContentfulImageContentBannerSliderModularBlock {
    id
    internal {
      type
    }
    heading
    contentBannerSlides {
      overline
      heading
      text {
        raw
      }
      image {
        ...SubBannerLargeImageFragment
      }
    }
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
  }

  fragment InstagramFeedModularBlockFragment on ContentfulInstagramFeedModularBlock {
    id
    internal {
      type
    }
    heading
    text
  }

  fragment LatestCommunityStoriesModularBlockFragment on ContentfulLatestCommunityStoriesModularBlock {
    id
    internal {
      type
    }
    heading
  }

  fragment MapLocationsModularBlockFragment on ContentfulMapLocationsModularBlock {
    id
    internal {
      type
    }
    heading
    text
    content {
      raw
    }
  }

  fragment VideoContentBannerModularBlockFragment on ContentfulVideoContentBannerModularBlock {
    id
    internal {
      type
    }
    video {
      url
      mimeType
    }
    content {
      raw
    }
    link {
      text
      pageUrl {
        ...LinkFragment
      }
    }
  }
`;
